import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useProjectContext } from "../../context/ProjectContext";
import {
	type CreateProjectPayload,
	projectService,
} from "../../services/projects";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

interface CreateProjectDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function CreateProjectDialog({
	open,
	onOpenChange,
}: CreateProjectDialogProps) {
	const { selectProject, refreshProjects } = useProjectContext();
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");

	const resetForm = () => {
		setName("");
		setDescription("");
	};

	const createProject = useMutation({
		mutationFn: (payload: CreateProjectPayload) =>
			projectService.create(payload),
		onSuccess: async (project) => {
			selectProject(project.id);
			await refreshProjects();
			toast.success("Project created", {
				description: `Added ${project.name}`,
			});
			onOpenChange(false);
			resetForm();
		},
		onError: (err) => {
			console.error("Failed to create project", err);
			toast.error("Could not create project", {
				description: "Please try again in a moment.",
			});
		},
	});

	const handleSubmit = () => {
		if (!name.trim()) return;
		createProject.mutate({
			name: name.trim(),
			description: description.trim() || undefined,
		});
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			resetForm();
		}
		onOpenChange(nextOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Create project</DialogTitle>
					<DialogDescription>
						Organize API keys and metrics under a project.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-2">
					<div className="space-y-2">
						<label htmlFor="project-name" className="text-sm font-medium">
							Name <span className="text-destructive">*</span>
						</label>
						<Input
							id="project-name"
							placeholder="e.g. Production"
							value={name}
							onChange={(e) => setName(e.target.value)}
							autoFocus
						/>
					</div>
					<div className="space-y-2">
						<label
							htmlFor="project-description"
							className="text-sm font-medium"
						>
							Description
						</label>
						<Textarea
							id="project-description"
							placeholder="Optional project description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							rows={3}
						/>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => handleOpenChange(false)}>
						Cancel
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={!name.trim() || createProject.isPending}
					>
						{createProject.isPending ? "Creating..." : "Create"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
